import { alpha, Box, Paper, Stack, SxProps, Theme } from '@mui/material'
import SecuritySearch from '../../../components/securitySearch/SecuritySearch'
import * as indicatorProto from '../../../proto/generated/indicator'
import React, { memo, useCallback, useContext, useMemo, useRef } from 'react'
import { OptionChainViewKey, ViewType } from '../../../assets/constants'
import { AppContext } from '../../../App'
import SecuritySearchShortcutsRegister from '../../../components/KeyboardShortcuts/SecuritySearchShortcuts'
import WatchlistShortcutRegister from '../../../components/KeyboardShortcuts/WatchlistShortcut'
import StrategyBuilderHeader from '../../../components/strategyBuilder/header'
import OptionChainHeader from '../../../components/optionChain/header'
import HorizontalScrollButtons from '../../../components/HorizontalScrollWrapper/horizontalScrollWrapper'
import StockBarActions from './stockBarActions'
import LayoutActions from './layoutActions'
import LoadAsLayoutButton from '../../../components/loadAsLayoutButton/loadAsLayoutButton'
import ScalperLayoutMenu from '../../scalper/scalperLayoutMenu/scalperLayoutMenu'
import ScalperActionBar from '../../scalper/scalperActionBar/scalperActionBar'
import OpenInterestHeader from '../openInterestHeader/OpenInterestHeader'
import StockBarChartOptions from './StockBarChartOptions'
import StockBarLtpSection from './StockBarLtpSection'
import { useStockBarRuntime } from './useStockBarRuntime'

interface Props {
    viewType: ViewType
    ivPlotTimeBucket: boolean
}

export const scrollButtonStyles: SxProps<Theme> = {
    position: 'absolute',
    height: '100%',
    minWidth: '30px',
    top: 0,
    zIndex: 99,
    color: (theme) => theme.palette.text.primary,
    background: (theme) => alpha(theme.palette.background.paper, 0.8),
    borderRadius: 0,
    padding: 0,
    opacity: 0
}

const StockBar = ({ ivPlotTimeBucket, viewType }: Props) => {
    const appContext = useContext(AppContext)
    const appContextRef = useRef(appContext)
    appContextRef.current = appContext

    const { activeChartId, disableChartOptions, disableSettingsButton, disableAlertButton } = useStockBarRuntime(ivPlotTimeBucket)
    const containerRef = useRef<HTMLDivElement>(undefined)

    const onIndicatorSelected = useCallback(
        (indicator: indicatorProto.Indicator) => {
            if (activeChartId && appContextRef.current.chart?.[activeChartId]) {
                appContextRef.current.chart[activeChartId]?.addIndicator(indicator)
            }
        },
        [activeChartId]
    )

    const isChartView = useMemo(() => viewType === ViewType.CHART, [viewType])
    const showChartActions = useMemo(
        () => [ViewType.CHART, ViewType.SYMBOL, ViewType.SPREAD, ViewType.SCALPER].includes(viewType),
        [viewType]
    )
    const isStandaloneView = useMemo(() => [ViewType.SYMBOL, ViewType.SPREAD].includes(viewType), [viewType])

    return (
        <HorizontalScrollButtons containerRef={containerRef}>
            <Paper
                sx={{
                    borderRadius: '4px 4px 0 0',
                    gridArea: 'header',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    position: 'relative',
                    minHeight: 40,
                    '&:hover .scroll-buttons': {
                        opacity: '1 !important'
                    }
                }}
                key="stockbar"
            >
                <WatchlistShortcutRegister />
                <SecuritySearchShortcutsRegister />
                <Box
                    className="stock-bar"
                    ref={containerRef}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        flex: 1,
                        padding: '2px 4px'
                    }}
                >
                    <Stack direction="row" justifyContent="start" alignItems="center" flex={1} gap={1.5}>
                        <SecuritySearch
                            view={viewType}
                            showIndexDropDown={viewType === ViewType.SIMULATOR}
                            isOnlyIndexSecurity={viewType === ViewType.SIMULATOR}
                            hideExpression={viewType === ViewType.SIMULATOR || viewType === ViewType.SCALPER}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 'fit-content',
                                flex: 1,
                                justifyContent: 'start',
                                height: '100%',
                                gap: 0,
                            }}
                        >
                            <StockBarLtpSection viewType={viewType} />
                            {viewType !== ViewType.FINANCIAL_DATA &&
                                viewType !== ViewType.SHAREHOLDER_HOLDINGS_DATA && (
                                <>
                                    {showChartActions && (
                                        <StockBarActions
                                            disableChartOptions={disableChartOptions}
                                            onIndicatorSelected={onIndicatorSelected}
                                            disableAlertButton={disableAlertButton}
                                        />
                                    )}
                                    {viewType === ViewType.OPTION_CHAIN && <OptionChainHeader />}
                                    {viewType === ViewType.STRATEGY_BUILDER && <StrategyBuilderHeader />}
                                    {viewType === ViewType.SIMULATOR && (
                                        <StrategyBuilderHeader optionChainViewKey={OptionChainViewKey.HISTORICAL} />
                                    )}
                                    {[ViewType.OPEN_INTEREST, ViewType.OPEN_INTEREST_CHANGE, ViewType.MULTI_STRIKES_CHART].includes(viewType) && (
                                        <OpenInterestHeader />
                                    )}
                                </>
                            )}
                        </Box>
                    </Stack>
                    {viewType !== ViewType.FINANCIAL_DATA &&
                        viewType !== ViewType.SHAREHOLDER_HOLDINGS_DATA &&
                        (isChartView || isStandaloneView) && (
                        <StockBarChartOptions
                            disableChartOptions={disableChartOptions}
                            disableSettingsButton={disableSettingsButton}
                            viewKey={activeChartId}
                        />
                    )}
                    {isChartView && (
                        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
                            <LayoutActions viewType={viewType} />
                        </Stack>
                    )}
                    {isStandaloneView && <LoadAsLayoutButton chartView={viewType.valueOf()} />}
                    {viewType === ViewType.SCALPER && <ScalperActionBar />}
                    {viewType === ViewType.SCALPER && <ScalperLayoutMenu />}
                </Box>
            </Paper>
        </HorizontalScrollButtons>
    )
}

export default memo(StockBar)
